import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Hero from './components/Hero';
import AboutPreview from './components/AboutPreview';
import SpeakingMediaSection from './components/SpeakingMediaSection';
import TestimonialsSection from './components/TestimonialsSection';
import { usePageSEO } from '@/hooks/usePageSEO';

const homeSchema = [
  {
    '@context': 'https://schema.org',
    '@type': 'ProfessionalService',
    name: 'BuildFit AI',
    url: 'https://jeffdume.com',
    description: 'Jeff Dumé, founder of BuildFit AI, helps organizations reduce mis-hires, improve retention, and create more consistent hiring decisions through human-led AI.',
    founder: { '@type': 'Person', name: 'Jeff Dumé' },
    address: {
      '@type': 'PostalAddress',
      addressLocality: 'Houston',
      addressRegion: 'TX',
      addressCountry: 'US',
    },
    areaServed: 'United States',
  },
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'What is BuildFit AI?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'BuildFit AI is a human-led platform designed to help organizations reduce mis-hires, improve retention, and create more consistent hiring decisions through structured evaluation and AI-supported insights. Every hiring decision remains fully human-led.',
        },
      },
      {
        '@type': 'Question',
        name: 'Who is Jeff Dumé?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Jeff Dumé is the founder of BuildFit AI and Mainframe Solutions. He speaks at HR Houston and the Gulf Coast Symposium on HR Issues. Based in Houston, Texas, he combines workforce strategy, operational thinking, and responsible AI to help organizations build systems that support sustainable growth.',
        },
      },
    ],
  },
];

export default function Home() {
  usePageSEO({
    title: 'Jeff Dumé | Founder of BuildFit AI — Human-Led Hiring Intelligence | Houston, TX',
    description: 'Jeff Dumé helps organizations reduce mis-hires, improve retention, and create more consistent hiring decisions through human-led AI. Founder of BuildFit AI and Mainframe Solutions. Speaker. Workforce Intelligence Advocate. Based in Houston, TX.',
    keywords: 'Jeff Dumé, BuildFit AI, human-led AI hiring, workforce intelligence, structured hiring, Mainframe Solutions, AI governance, HR Houston speaker, Gulf Coast Symposium, workforce strategy Houston',
    canonical: '/',
    ogTitle: 'Jeff Dumé | Founder of BuildFit AI — Human-Led Hiring Intelligence',
    ogDescription: 'Helping organizations reduce mis-hires, improve retention, and create more consistent hiring decisions through human-led AI. AI-assisted. Never AI-decisive.',
    schema: homeSchema,
    breadcrumbs: [],
  });

  return (
    <div className='bg-charcoal'>
      <Navbar />
      <main>
        <Hero />

        {/* Trust Badge Strip */}
        <section className="py-6 md:py-8 border-y border-white/5">
          <div className="w-full px-4 sm:px-6 md:px-10 lg:px-16 max-w-6xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 md:gap-10">
              {[
                { icon: 'ri-linkedin-box-fill', label: '50+ LinkedIn\nRecommendations' },
                { icon: 'ri-shield-check-line', label: 'Certified Federal\nContractor' },
                { icon: 'ri-mic-line', label: 'Speaker\nHR Houston' },
                { icon: 'ri-presentation-line', label: 'HR Issues Speaker\nGulf Coast Symposium' },
              ].map((badge) => (
                <div key={badge.label} className="flex flex-col items-center justify-center gap-2 text-white/60 hover:text-gold transition-colors cursor-default py-2">
                  <div className="w-10 h-10 flex items-center justify-center shrink-0 bg-gold/10 rounded-xl">
                    <i className={`${badge.icon} text-gold text-base`}></i>
                  </div>
                  <span className="text-xs md:text-sm font-medium text-center leading-snug">{badge.label.split('\n').map((line, i) => (
                    <span key={i} className="block">{line}</span>
                  ))}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        <AboutPreview />
        <SpeakingMediaSection />
        <TestimonialsSection />
      </main>
      <Footer />
    </div>
  );
}